import {
  Activity,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  House,
  LogOut,
  type LucideIcon,
  Menu,
  MessageSquare,
  Shield,
  User,
  X,
} from "lucide-react";
import { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";

import { FeedbackButton } from "@/components/feedback-button";
import { JJWordmark } from "@/components/jj-wordmark";
import { StatusBadge, type StatusBadgeKind } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { WelcomeDialog, WelcomeDialogTrigger } from "@/components/welcome-dialog";
import { PresenterVotingDialog } from "@/features/voting";
import { useLogout, useMe } from "@/lib/api";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

type NavItem = {
  href: string;
  label: string;
  icon: LucideIcon;
  status: StatusBadgeKind;
};

// TEMPLATE_TODO: replace with this app's real navigation. `status` drives the
// greyed-out "coming soon" treatment: anything not "live" reads as roadmap.
const NAV: NavItem[] = [
  { href: "/home", label: "Home", icon: House, status: "live" },
  { href: "/guide", label: "Guide", icon: BookOpen, status: "live" },
];

const ADMIN_NAV: NavItem[] = [
  { href: "/admin/users", label: "Users", icon: Shield, status: "live" },
  { href: "/admin/activity", label: "Activity", icon: Activity, status: "live" },
  { href: "/admin/feedback", label: "Feedback", icon: MessageSquare, status: "live" },
];

// TEMPLATE_TODO: set this app's top-bar eyebrow (product family · app name).
const EYEBROW = "Vision Deliver Intelligence · Future of Work Lab";

// TEMPLATE_TODO: extend with this app's routes as you add nav entries.
function currentTitle(pathname: string): string {
  if (pathname.startsWith("/home")) {
    return "Home";
  }
  if (pathname.startsWith("/guide")) {
    return "Guide";
  }
  if (pathname.startsWith("/profile")) {
    return "Profile";
  }
  if (pathname.startsWith("/admin/users")) {
    return "User management";
  }
  if (pathname.startsWith("/admin/activity")) {
    return "Activity";
  }
  if (pathname.startsWith("/admin/feedback")) {
    return "Feedback";
  }
  if (pathname.startsWith("/admin")) {
    return "Admin";
  }
  return "Workspace";
}

const COLLAPSE_KEY = "vision-deliver-intelligence.sidebar.collapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Authenticated app chrome: a collapsible sidebar (white when expanded,
 * J&J-red icon rail when collapsed), a slim top bar with eyebrow + per-route
 * title, and the shared demo overlays (feedback tab, welcome dialog) mounted
 * once. Pages render inside the scrollable <main> via the router Outlet.
 */
export function AppShell() {
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const { data: me } = useMe();
  const logoutMutation = useLogout();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggleCollapsed = () =>
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        logger.debug("[shell]", "could not persist sidebar state");
      }
      return next;
    });

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  const isAdmin = me?.role === "admin";

  return (
    // h-screen + overflow-hidden bounds the page to one viewport; pages that
    // need to scroll do so inside <main>, so the scrollbar lives in the
    // content column rather than next to the sidebar.
    <div className="bg-background flex h-screen overflow-hidden">
      {/* Sidebar — desktop */}
      <aside
        className={cn(
          "sticky top-0 hidden h-screen shrink-0 flex-col border-r transition-[width,background-color,color] duration-200 ease-out lg:flex",
          collapsed
            ? "bg-primary text-primary-foreground border-primary w-16"
            : "bg-card border-border w-64",
        )}
      >
        <SidebarContent
          pathname={pathname}
          isAdmin={isAdmin}
          userEmail={me?.email}
          onLogout={handleLogout}
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
        />
      </aside>

      {/* Sidebar — mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="border-border bg-card absolute top-0 left-0 flex h-full w-72 flex-col border-r shadow-xl">
            <SidebarContent
              pathname={pathname}
              isAdmin={isAdmin}
              userEmail={me?.email}
              onLogout={handleLogout}
              onNavigate={() => setMobileOpen(false)}
              collapsed={false}
            />
          </aside>
        </div>
      )}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="border-border bg-card flex items-center justify-between gap-3 border-b px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <p className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
                {EYEBROW}
              </p>
              <h1 className="font-heading text-foreground truncate text-base font-medium tracking-tight sm:text-lg">
                {currentTitle(pathname)}
              </h1>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* The welcome trigger stays visible so anyone can re-open the
                demo overview from the top bar regardless of role. */}
            <WelcomeDialogTrigger />
          </div>
        </header>

        {/* Renders nothing on its own — pops the welcome panel on first visit. */}
        <WelcomeDialog />

        <main className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
          <Outlet context={{ me }} />
        </main>
      </div>

      {/* Floating feedback tab — visible on every authenticated page.
          Submissions land in the `feedback` table via /admin/feedback. */}
      <FeedbackButton />
    </div>
  );
}

function SidebarContent({
  pathname,
  isAdmin,
  userEmail,
  onLogout,
  onNavigate,
  collapsed,
  onToggleCollapsed,
}: {
  pathname: string;
  isAdmin: boolean;
  userEmail?: string;
  onLogout: () => void;
  onNavigate?: () => void;
  collapsed: boolean;
  onToggleCollapsed?: () => void;
}) {
  return (
    <>
      <div
        className={cn(
          "flex items-center border-b",
          collapsed
            ? "flex-col gap-1 border-white/15 px-2 py-3"
            : "border-border justify-between gap-2 px-5 py-5",
        )}
      >
        {collapsed ? (
          <>
            {/* Clickable J&J mark — same destination as the expanded wordmark. */}
            <Link
              to="/home"
              onClick={onNavigate}
              aria-label="Go home"
              className="font-heading text-primary-foreground block py-1 text-base font-bold tracking-tight hover:opacity-80"
            >
              J&amp;J
            </Link>
            {onToggleCollapsed && (
              <button
                onClick={onToggleCollapsed}
                aria-label="Expand sidebar"
                className="text-primary-foreground flex h-7 w-full items-center justify-center rounded-md transition-colors hover:bg-white/10"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </>
        ) : (
          <>
            {/* Wordmark routes home — the standard "click the logo" pattern. */}
            <Link
              to="/home"
              onClick={onNavigate}
              aria-label="Go home"
              className="rounded-sm hover:opacity-80 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              <JJWordmark size="sm" />
            </Link>
            {onNavigate ? (
              <button
                onClick={onNavigate}
                className="text-muted hover:text-foreground"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            ) : (
              onToggleCollapsed && (
                <button
                  onClick={onToggleCollapsed}
                  className="text-muted hover:bg-warm-gray-02 hover:text-foreground inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors"
                  aria-label="Collapse sidebar"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              )
            )}
          </>
        )}
      </div>

      <nav className={cn("flex-1 overflow-y-auto py-4", collapsed ? "px-2" : "px-3")}>
        {!collapsed && (
          <p className="text-muted px-2 pb-2 text-[10px] font-medium tracking-[0.18em] uppercase">
            Workspace
          </p>
        )}
        <ul className="space-y-0.5">
          {NAV.map((item) => (
            <NavRow
              key={item.href}
              item={item}
              pathname={pathname}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          ))}
        </ul>

        {isAdmin && (
          <>
            {!collapsed ? (
              <p className="text-muted px-2 pt-6 pb-2 text-[10px] font-medium tracking-[0.18em] uppercase">
                Admin
              </p>
            ) : (
              <div className="my-3 border-t border-white/15" aria-hidden />
            )}
            <ul className="space-y-0.5">
              {ADMIN_NAV.map((item) => (
                <NavRow
                  key={item.href}
                  item={item}
                  pathname={pathname}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                />
              ))}
            </ul>
          </>
        )}
      </nav>

      {collapsed ? (
        <div className="space-y-1 border-t border-white/15 px-2 py-3">
          {/* Presenter-side voting control (QR + reveal + reset live inside
              the dialog) — admin-gated like the other demo controls. */}
          {isAdmin && (
            <PresenterVotingDialog
              iconOnly
              triggerClassName="text-primary-foreground flex h-10 w-full items-center justify-center rounded-md transition-colors hover:bg-white/10"
            />
          )}
          <Link
            to="/profile"
            aria-label="Profile"
            className={cn(
              "flex h-10 w-full items-center justify-center rounded-md transition-colors",
              pathname === "/profile"
                ? "text-primary bg-white"
                : "text-primary-foreground hover:bg-white/10",
            )}
          >
            <User className="h-4 w-4" />
          </Link>
          <button
            onClick={onLogout}
            aria-label="Sign out"
            className="text-primary-foreground flex h-10 w-full items-center justify-center rounded-md transition-colors hover:bg-white/10"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="border-border space-y-2 border-t px-3 py-4">
          <div className="px-2 pb-1">
            <p className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
              Demo legend
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              {/* "Real data" (not "Live") so the legend isn't confused with
                  per-feature build-status badges elsewhere. */}
              <StatusBadge kind="live" label="Real data" />
              <StatusBadge kind="mock" />
            </div>
          </div>
          {isAdmin && (
            <PresenterVotingDialog triggerClassName="text-foreground hover:bg-warm-gray-02 flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors" />
          )}
          <Link
            to="/profile"
            onClick={onNavigate}
            className={cn(
              "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
              pathname === "/profile"
                ? "bg-warm-gray-02 text-foreground"
                : "text-foreground hover:bg-warm-gray-02",
            )}
          >
            <User className="h-4 w-4 shrink-0" />
            <span className="min-w-0 truncate">{userEmail ?? "Profile"}</span>
          </Link>
          <button
            onClick={onLogout}
            className="text-foreground hover:bg-warm-gray-02 flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </>
  );
}

function NavRow({
  item,
  pathname,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  pathname: string;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  const isActive = pathname === item.href || pathname.startsWith(item.href + "/");
  // Anything that isn't fully wired (mock data OR roadmap) is treated as
  // "coming soon" — greyed out so the live items pop.
  const isComingSoon = item.status !== "live";

  const baseRow = "group flex items-center rounded-md transition-colors";
  const sizing = collapsed
    ? "h-10 w-full justify-center"
    : "justify-between gap-2 px-3 py-2 text-sm";

  // Tone differs by container — collapsed uses the red rail, expanded warm-gray.
  const collapsedTone = isActive
    ? "bg-white text-primary"
    : isComingSoon
      ? "text-primary-foreground/60 hover:text-primary-foreground hover:bg-white/10"
      : "text-primary-foreground hover:bg-white/10";
  const expandedTone = isActive
    ? "bg-primary text-primary-foreground"
    : isComingSoon
      ? "text-warm-gray-07 hover:text-foreground hover:bg-warm-gray-02"
      : "text-foreground hover:bg-warm-gray-02";

  return (
    <li>
      <Link
        to={item.href}
        onClick={onNavigate}
        title={collapsed ? item.label : undefined}
        aria-label={collapsed ? item.label : undefined}
        className={cn(baseRow, sizing, collapsed ? collapsedTone : expandedTone)}
      >
        {collapsed ? (
          <Icon className={cn("h-4 w-4 shrink-0", isComingSoon && !isActive && "opacity-70")} />
        ) : (
          <span className="flex min-w-0 items-center gap-2.5">
            <Icon
              className={cn(
                "h-4 w-4 shrink-0",
                isActive ? "text-primary-foreground" : isComingSoon ? "opacity-60" : "",
              )}
            />
            <span className="truncate">{item.label}</span>
          </span>
        )}
      </Link>
    </li>
  );
}
